import {
  LitElement,
  paths,
  html,
  unsafeHTML,
  css,
  utils,
  ifDefined,
  t,
} from '@hh/global.js'
const { externalVideoEmbedSrc, getPreviousSibling } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    posterImageMobile: {
      type: String,
      attribute: 'poster-image-mobile',
    },
    useExternalPlayImage: {
      type: Boolean,
      attribute: 'use-external-play-image',
    },
    enableSound: {
      type: Boolean,
      attribute: 'enable-sound',
    },
    use_youtube_nocookie_link: {
      type: Boolean,
    },
    requireConsent: {
      type: Boolean,
      attribute: 'require-consent',
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }
  

  constructor() {
    super()
    this.consentGiven = false
    this.consentCheckAttempts = 0
    this.maxConsentCheckAttempts = 5
    this.oneTrustListenerSetup = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
    
    this.checkConsent()
    this.setupDelayedConsentCheck()
  }

  setupDelayedConsentCheck() {
    if (!this.requireConsent || this.consentGiven) return

    setTimeout(() => {
      if (!this.consentGiven && this.consentCheckAttempts < this.maxConsentCheckAttempts) {
        this.consentCheckAttempts++
        this.checkConsent()
        
        if (this.requireConsent && window.OneTrust && !this.oneTrustListenerSetup) {
          this.oneTrustListenerSetup = true
          window.OneTrust.OnConsentChanged(() => {
            this.checkConsent()
            this.requestUpdate()
          })
        }

        if (!this.consentGiven) {
          this.setupDelayedConsentCheck()
        }
        this.requestUpdate()
      }
    }, 1000)
  }

  firstUpdated() {
    if (this.host === 'vimeo') {
      this.videoElement = new Vimeo.Player(this.videoElement)
    }
  }

  checkConsent() {
    if (!this.requireConsent) {
      this.consentGiven = true
      return
    }

    if (typeof window !== 'undefined' && window.OneTrust) {
      const activeGroups = window.OnetrustActiveGroups
      this.consentGiven = activeGroups.includes('C0003') || activeGroups.includes('C0004')
    } else {
      this.consentGiven = false
    }
  }

  handlePlayClick() {
    if (this.playing == false) {
      this.playing = true
    }
    let overlay = this.renderRoot?.querySelector('.video-overlay')
    if (overlay) overlay.style.display = 'none'

    if (this.host === 'vimeo') {
      this.videoElement.play()
    } else if (this.host === 'youtube') {
      this.videoElement.src = this.videoElement.src.replace(
        'autoplay=0',
        'autoplay=1'
      )
      
      this.videoElement.focus()
    }
  }

  handleConsentPlayClick() {
    if (window.OneTrust && window.OneTrust.ToggleInfoDisplay) {
      window.OneTrust.ToggleInfoDisplay()
    }
  }

  generatePosterImage() {
    const hasMobileAndDesktop = this.posterImage && this.posterImageMobile
    const mobileSource = this.posterImageMobile
    const desktopSource = this.posterImage

    return `${hasMobileAndDesktop ? `<picture>` : ''}
      ${
        hasMobileAndDesktop
          ? `<source media="(min-width: 1024px)" srcset="${desktopSource}">`
          : ''
      }
      <img src="${mobileSource || desktopSource}" alt="">
    ${hasMobileAndDesktop ? `</picture>` : ''}`
  }

  /**
   * Generate the YT video poster image for elements who use YT videos and don't have a poster image set in the shop admin
   * @returns {string} Image HTML
   */
  generateYTPosterImage() {
    if (!this.id) {
      return ''
    }

    return `<img src="//img.youtube.com/vi/${this.id}/hqdefault.jpg" alt="" loading="lazy">`
  }

  generateConsentPlaceholder() {
    const customImage = this.posterImage
      ? this.generatePosterImage()
      : this.generateYTPosterImage()
    const playImage = this.useExternalPlayImage
      ? paths.playExternal
      : paths.play

    const consentMessage = t('video.consent.message', 
      "This video is hosted by YouTube and requires cookies to play. Please accept Functional or Marketing cookies to view this content.")
    const consentButton = t('video.consent.button', 
      "Accept cookies and play video")

    return html`
      <div class="video-consent-overlay">
        <div class="video-consent-content">
          ${this.title ? html`<h2>${this.title}</h2>` : html``}
          <div class="video-consent-message">
            <p>${consentMessage}</p>
          </div>
          <button
            class="video-consent-button"
            type="button"
            @click="${this.handleConsentPlayClick}"
          >
            ${consentButton}
          </button>
        </div>
        ${unsafeHTML(customImage)}
      </div>
    `
  }

  generatePosterWithText() {
    const isYTVideo = ['youtube'].includes(this.host)
    if (!isYTVideo && !this.posterImage) {
      return html``
    }

    const customImage = this.posterImage
      ? this.generatePosterImage()
      : this.generateYTPosterImage()
    const playImage = this.useExternalPlayImage
      ? paths.playExternal
      : paths.play
    return html`
      <div class="video-overlay">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        ${this.description ? html`<p>${this.description}</p>` : html``}
        <button
          class="video-poster-button${this.useExternalPlayImage
            ? ' video-poster-button--external'
            : ''}"
          type="button"
          @click="${this.handlePlayClick}"
          aria-label="Play"
        >
          ${unsafeHTML(playImage || html`►`)}
        </button>
        ${unsafeHTML(customImage)}
      </div>
    `
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        poster="${ifDefined(this.posterImage)}"
        preload="none"
        muted="${this.enableSound === false}"
        aria-label="${this.description}"
      >
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop,
          this.enableSound === false,
          this.use_youtube_nocookie_link
        )}"
        title="Video"
      >
      </iframe>
    `
  }

  render() {
    if (this.requireConsent && !this.consentGiven && ['vimeo', 'youtube'].includes(this.host)) {
      return this.generateConsentPlaceholder()
    }

    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay ? html`` : this.generatePosterWithText()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit, cover);
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    h2,
    p {
      z-index: 1;
      color: white;
    }
    h2 {
      font-size: 3rem;
      margin-bottom: 1.5rem;
    }
    p {
      font-size: 1.5rem;
      width: 25%;
      text-align: center;
      margin-bottom: 3rem;
    }
    .video-overlay img {
      position: absolute;
      top: 50%;
      max-width: 100%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      width: 100%;
      height: auto;
      filter: brightness(0.5);
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border: 1px solid white;
      border-radius: 60px;
      cursor: pointer;
      width: 60px;
      height: 60px;
      padding: 1rem;
      display: flex;
      justify-content: center;
      z-index: 1;
    }
    .video-poster-button--external {
      border: none;
      height: auto;
      padding: 0;
      width: auto;
    }
    .video-poster-button svg {
      position: relative;
      width: 30px;
      height: 30px;
      fill: white;
      stroke: white;
    }
    .video-poster-button .icon-play-external {
      color: var(--hh-color-day, #fff);
      height: 48px;
      width: 48px;
    }

    @media screen and (min-width: 1024px) {
      .video-poster-button .icon-play-external {
        height: 96px;
        width: 96px;
      }
    }
    .video-consent-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background-color: rgba(0, 0, 0, 0.8);
    }

    .video-consent-content {
      z-index: 2;
      text-align: center;
      color: white;
      padding: 2rem;
      max-width: 500px;
    }

    .video-consent-content h2 {
      font-size: 2rem;
      margin-bottom: 1rem;
      color: white;
    }

    .video-consent-message p {
      font-size: 1.8rem;
      line-height: 1.5;
      margin-bottom: 2rem;
      color: rgba(255, 255, 255, 0.9);
      width: 100%;
    }

    .video-consent-button {
      background-color: var(--hh-background, #FF0000);
      border: none;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      padding: 12px 24px;
      border-radius: 50px;
      transition: background-color 0.3s ease, transform 0.2s ease;
      border: 5px solid var(--hh-background, #FF0000);    
      letter-spacing: 0.15em;
      font: 800 1.4rem/1.4 'VAG Rounded Next HH', sans-serif;
      text-transform: uppercase;
    }

    .video-consent-button:hover {
      background-color: #fff;
      color: var(--hh-background, #FF0000);
    }

    .video-consent-button:active {
      transform: scale(0.98);
    }

    .video-consent-overlay img {
      position: absolute;
      top: 50%;
      max-width: 100%;
      left: 0;
      right: 0;
      transform: translateY(-50%);
      width: 100%;
      height: auto;
      filter: brightness(0.3);
      z-index: 1;
    }

    @media screen and (max-width: 768px) {
      .video-consent-content {
        padding: 1rem;
      }

      .video-consent-content h2 {
        font-size: 1.5rem;
      }

      .video-consent-message p {
        font-size: 1rem;
      }

      .video-consent-button {
        font-size: 0.9rem;
        padding: 10px 20px;
      }
    }  
  `
}

if (!customElements.get('hh-adaptive-video')) {
  customElements.define('hh-adaptive-video', AdaptiveVideo)
};

